import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";

const empty = {
    name: "", slug: "", description: "",
    cover_image_url: "", order: 0, is_active: true,
};

export default function AdminShopCategories() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = () => api.get("/shop-categories?active_only=false").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing("new"); setForm(empty); };
    const openEdit = (c) => { setEditing(c.id); setForm({ ...c }); };
    const close = () => { setEditing(null); setForm(empty); };

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form, order: parseInt(form.order) || 0 };
        try {
            if (editing === "new") await api.post("/shop-categories", payload);
            else await api.put(`/shop-categories/${editing}`, payload);
            toast.success("Saved"); close(); load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this shop category? Products in it remain but the tile disappears from /shop.")) return;
        try { await api.delete(`/shop-categories/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/shop-categories/${c.id}`, { ...c, is_active: !c.is_active });
            toast.success(c.is_active ? "Disabled — hidden from site" : "Enabled — now visible");
            load();
        } catch { toast.error("Update failed"); }
    };

    return (
        <div data-testid="admin-shop-categories">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display text-3xl">Shop Categories</h2>
                    <p className="text-sm text-ink-soft mt-1">
                        The tiles shown on <code className="text-xs">/shop</code>. Click a tile → products in that category.
                        The category <strong>Name</strong> must match the "Category" on each product exactly.
                    </p>
                </div>
                <button onClick={openNew} className="btn-pink" data-testid="add-shop-category"><Plus size={14} /> New</button>
            </div>

            <div className="space-y-2">
                {items.map((c, i) => (
                    <div key={c.id} className="bg-white border border-[#EAE3DE] p-4 flex items-center gap-4" data-testid={`shop-category-row-${i}`}>
                        {c.cover_image_url ? (
                            <img src={c.cover_image_url} alt={c.name} className="w-16 h-20 object-cover" />
                        ) : (
                            <div className="w-16 h-20 bg-blush-pale flex items-center justify-center text-[9px] text-ink-soft">no image</div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-lg">{c.name}</p>
                            <p className="text-xs text-ink-soft">/shop/{c.slug} · order {c.order}</p>
                        </div>
                        <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${c.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {c.is_active ? "Active" : "Hidden"}
                        </span>
                        <div className="flex gap-3 items-center ml-2">
                            <button onClick={() => toggleActive(c)} className={c.is_active ? "text-green-600 hover:text-blush" : "text-gray-400 hover:text-blush"} title={c.is_active ? "Disable (hide from site)" : "Enable (show on site)"} data-testid={`toggle-shop-category-${i}`}>
                                {c.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            <button onClick={() => openEdit(c)} data-testid={`edit-shop-category-${i}`}><Edit size={16} /></button>
                            <button onClick={() => remove(c.id)} data-testid={`delete-shop-category-${i}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="text-center text-ink-soft py-12">No shop categories yet — click "New".</p>}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Shop Category</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Name</label>
                                    <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="e.g. Bento Cakes" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Slug (URL)</label>
                                    <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="bento-cakes" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Description (optional)</label>
                                <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <ImageUpload value={form.cover_image_url} onChange={(v) => setForm({ ...form, cover_image_url: v })} label="Category Cover (portrait, ~1200×1500)" testIdPrefix="shop-category-image" />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Sort order</label>
                                    <input type="number" className="w-full px-4 py-3 border border-[#EAE3DE]" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                                </div>
                                <label className="flex items-end gap-2 pb-3">
                                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                    <span className="text-sm">Active (visible on /shop)</span>
                                </label>
                            </div>
                            <button type="submit" className="btn-pink w-full">Save Shop Category</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
